import { Component, Input } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { exhaustMap, filter, finalize, Subject, Subscription, tap } from 'rxjs';
import { IecVoteId, iecVoteOption, IIecBallotVote, IProxyVoteRequest, IVoteRequest } from '../../shared/models/iec/iec-ballot-vote';
import { IecBallotService } from '../../shared/services/iec-ballot.service';
import { HttpEventType } from '@angular/common/http';
import { IUser } from '../../../../shared/models/user';
import { AuthService } from '../../../../shared/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { UserUtility } from '../../../../shared/utilities/user-utility';
import { IFileUpload } from '../../../controls/files-upload/files-upload.component';


@Component({
    selector: 'app-iec-place-vote',
    templateUrl: './iec-place-vote.component.html',
    styleUrl: './iec-place-vote.component.scss',
    standalone: false
})


export class IecPlaceVoteComponent {

  @Input() userVote!: IIecBallotVote;
  @Input() user!: IUser;

  @Input() votes?: IIecBallotVote[] = [];
  @Input() isProxyVote: boolean = false;
  submit$ = new Subject<void>();
  isSubmitting = false;

  subscriptions: Subscription = new Subscription();

  voteOptionForm = new FormGroup({
    inFavourOfSelectCtrl: new FormControl(false),
    againstSelectCtrl: new FormControl(false),
    abstainSelectCtrl: new FormControl(false),
    inFavourOfNoteCtrl: new FormControl(''),
    againstNoteCtrl: new FormControl(''),
    abstainNoteCtrl: new FormControl(''),
    authorisationDocumentCtrl: new FormControl()
  });

  isFormValid: boolean = false;



  noteTitle = "Please enter a reason for your decision";

  inFavourOf = iecVoteOption["IN_FAVOUR_OF"];
  against = iecVoteOption["AGAINST"];
  abstain = iecVoteOption["ABSTAIN"];

  committeeMembersLabel: string = 'Committee Members';
  committeeMembersDescription: string = '';

  currentUser: IUser | null = null;

  isUserPmOrAbove: boolean = false;


  isSingleProxyVote: boolean = true;

  constructor(private activeModal: NgbActiveModal,
              private _iecBallotService: IecBallotService,
              public _authService: AuthService,
              private _toastService: ToastService){
    this.initUser();
}

  get inFavourOfSelect(){
    return this.voteOptionForm.get('inFavourOfSelectCtrl');
  }
  get againstSelect() {
    return this.voteOptionForm.get('againstSelectCtrl');
  }
  get abstainSelect() {
    return this.voteOptionForm.get('abstainSelectCtrl');
  }

  get inFavourOfNote() {
    return this.voteOptionForm.get('inFavourOfNoteCtrl');
  }
  get againstNote() {
    return this.voteOptionForm.get('againstNoteCtrl');
  }
  get abstainNote() {
    return this.voteOptionForm.get('abstainNoteCtrl');
  }
  get authorisationDocument():  IFileUpload {
    return (this.voteOptionForm.get('authorisationDocumentCtrl') as FormControl).value;
  }


  ngOnInit(){
    this.loadExistingVote();
    this.setCommitteeMembersDescription(this.votes??[]);
    this.isSingleProxyVote = this.votes?.length === 1;
    this.voteOptionForm.valueChanges.subscribe(values => {
      this.validate();
    });

    this.setupSubmitHandler();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }



  loadExistingVote() {

    if (!this.userVote) return;

    if (this.userVote?.iecVoteOptionName == IecVoteId.IN_FAVOUR_OF) {
      this.inFavourOfSelect?.setValue(true);
      this.inFavourOfNote?.setValue(this.userVote?.remarks);
    }

    if (this.userVote?.iecVoteOptionName == IecVoteId.AGAINST) {
      this.againstSelect?.setValue(true);
      this.againstNote?.setValue(this.userVote?.remarks);
    }

    if (this.userVote?.iecVoteOptionName == IecVoteId.ABSTAIN) {
      this.abstainSelect?.setValue(true);
      this.abstainNote?.setValue(this.userVote?.remarks);
    }

  }

  initUser() {
      const currentUserSubscription =
        this._authService.currentUserSubject.subscribe((user) => {
          this.currentUser = user;
          if (this.currentUser) {
            this.isUserPmOrAbove = UserUtility.userIsPmOrAbove(this.currentUser);
          }
        });
      this.subscriptions.add(currentUserSubscription);
  }

  setCommitteeMembersDescription(committeeMembers: IIecBallotVote[]) {
      if (committeeMembers.length > 0) {
        this.committeeMembersLabel = committeeMembers.length > 1 ? 'Committee Members:' : 'Committee Member:';


        let firstCm = committeeMembers[0].committeeMember;
        let otherCms = '';

        if (committeeMembers.length > 1) {
          otherCms = ` + ${committeeMembers.length - 1} other${committeeMembers.length > 2 ? 's' : ''}`;
        }
        this.committeeMembersDescription = firstCm + otherCms;
      } else {
        this.committeeMembersDescription = '';
      }
  }

  selectInFavourOf() {
    this.reset();
    this.inFavourOfSelect?.setValue(true);
  }

  selectAgainst() {
    this.reset();
    this.againstSelect?.setValue(true);
  }

  selectAbstain() {
    this.reset();
    this.abstainSelect?.setValue(true);
  }

  reset() {
    this.inFavourOfSelect?.setValue(false);
    this.againstSelect?.setValue(false);
    this.abstainSelect?.setValue(false);
  }

  close() {
    this.activeModal.close('cancel');
  }

  dismiss(vote: string) {
    this.activeModal.dismiss(vote);
  }




  getUserVote() : IVoteRequest {
    return  {
      ballotId: this.userVote?.iecBallotId,
      voteId: this.userVote?.id??'',
      iecVoteOptionName : this.inFavourOfSelect?.value ? IecVoteId.IN_FAVOUR_OF : this.againstSelect?.value ? IecVoteId.AGAINST : this.abstainSelect?.value ? IecVoteId.ABSTAIN : IecVoteId.NO_RESPONSE,
      comments: this.inFavourOfSelect?.value ? this.inFavourOfNote?.value??'' : this.againstSelect?.value ? this.againstNote?.value??'' : this.abstainSelect?.value ? this.abstainNote?.value??'' : '',
    }
  }


  onSubmitClick(): void {
    this.submit$.next();
  }

  setupSubmitHandler(): void {
    this.subscriptions.add(
      this.submit$
        .pipe(
          filter(() => !this.isSubmitting),
          tap(() => this.isSubmitting = true),
          exhaustMap(() => {
            return this.isProxyVote
              ? this._iecBallotService.submitProxyVote(this.getProxyVoteRequest())
              : this._iecBallotService.placeVote(this.getUserVote());
          }),
          finalize(() => {
            this.isSubmitting = false;
          })
        )
        .subscribe({
          next: (response: any) => this.handleSuccess(response),
          error: () => {
            this._toastService.showOnFailedPlacingVote();
            this.close();
          },
        })
    );
  }


  handleSuccess(response: any): void {
    if (this.isProxyVote) {
      if (response.type === HttpEventType.Response) {
        this._toastService.showOnSuccessfulPlacingVote();
        setTimeout(() => this.close(), 300);
      }
    } else {
      if (response?.success) {
        this._toastService.showOnSuccessfulPlacingVote();
        this.dismiss(this.getUserVote().iecVoteOptionName);
        setTimeout(() => window.location.reload(), 1000);
      }
    }
  }



  getProxyVoteRequest() : IProxyVoteRequest {
    let ballotId = this.votes && this.votes?.length > 0 ? this.votes[0].iecBallotId : '';
    return  {
      ballotId: ballotId,
      voteIds: this.votes?.map(vote => vote.id)??[],
      voteOption : this.inFavourOfSelect?.value ? IecVoteId.IN_FAVOUR_OF : this.againstSelect?.value ? IecVoteId.AGAINST : this.abstainSelect?.value ? IecVoteId.ABSTAIN : IecVoteId.NO_RESPONSE,
      comments: this.inFavourOfSelect?.value ? this.inFavourOfNote?.value??'' : this.againstSelect?.value ? this.againstNote?.value??'' : this.abstainSelect?.value ? this.abstainNote?.value??'' : '',
      file: this.authorisationDocument?.files[0]
    }
  }

  validate() {
    this.isFormValid = ((!this.isProxyVote || (this.isProxyVote && this.authorisationDocument != null && this.authorisationDocument.files.length != 0 && !this.authorisationDocument.hasError)) &&
        ((this.inFavourOfSelect?.value) ||
        (this.againstSelect?.value && this.againstNote?.value != '') ||
        (this.abstainSelect?.value)
        ))??false;
  }
}
